import { IS_DEVELOPMENT_ENV, IS_PLAYWRIGHT_RUN, IS_TESTING_ENV } from '@/config/environments.config'

export const AMPLITUDE_DEPLOYMENT_KEY = process.env.NEXT_PUBLIC_AMPLITUDE_DEPLOYMENT_KEY

/** How long a user action waits on a variant before acting on the default instead. */
export const EXPERIMENT_VARIANT_TIMEOUT_MS = 2000

/**
 * A/B test (Amplitude Experiment) definitions.
 *
 * Every experiment declares its flag key, the variants it can return, and the
 * variant that represents the *current* production behavior. The default variant
 * is what the app renders until (or unless) Amplitude answers, so shipping an
 * experiment can never change behavior on its own.
 *
 * Resolution happens in `utils/experiments.utils.ts` — that is the single place
 * to wire up the Amplitude Experiment client.
 */
export type ExperimentDefinition = {
  /** Amplitude Experiment flag key */
  key: string
  /** Every variant value the flag is allowed to return */
  variants: readonly string[]
  /** Variant used until Amplitude answers — always the current/control behavior */
  defaultVariant: string
}

/**
 * Ticket type description A/B test.
 *
 * `control` keeps descriptions inline beneath the ticket title (current behavior).
 * `tooltip` hides them behind an (i) icon next to the ticket title.
 */
export const TICKET_DESCRIPTION_TOOLTIP_VARIANTS = {
  control: 'control',
  tooltip: 'tooltip',
} as const

export const TICKET_DESCRIPTION_TOOLTIP_EXPERIMENT: ExperimentDefinition = {
  key: 'ticket-description-tooltip',
  variants: Object.values(TICKET_DESCRIPTION_TOOLTIP_VARIANTS),
  defaultVariant: TICKET_DESCRIPTION_TOOLTIP_VARIANTS.control,
}

export const PORTAL_PASS_INTERRUPT_VARIANTS = {
  control: 'control',
  banner: 'banner',
} as const

export const PORTAL_PASS_INTERRUPT_EXPERIMENT: ExperimentDefinition = {
  key: 'portal-pass-banner',
  variants: Object.values(PORTAL_PASS_INTERRUPT_VARIANTS),
  defaultVariant: PORTAL_PASS_INTERRUPT_VARIANTS.control,
}

/**
 * Add-ons "Skip this step" button A/B test.
 *
 * `control` shows the skip button in the add-ons header (current behavior).
 * `hidden` removes it, leaving the step's main button as the only way forward.
 */
export const ADD_ONS_SKIP_BUTTON_VARIANTS = {
  control: 'control',
  hidden: 'hidden',
} as const

export const ADD_ONS_SKIP_BUTTON_EXPERIMENT: ExperimentDefinition = {
  key: 'add-ons-skip-button',
  variants: Object.values(ADD_ONS_SKIP_BUTTON_VARIANTS),
  defaultVariant: ADD_ONS_SKIP_BUTTON_VARIANTS.control,
}

/** Every experiment the app resolves. Add new experiments here so CI pins them too. */
export const EXPERIMENTS: readonly ExperimentDefinition[] = [
  TICKET_DESCRIPTION_TOOLTIP_EXPERIMENT,
  PORTAL_PASS_INTERRUPT_EXPERIMENT,
  ADD_ONS_SKIP_BUTTON_EXPERIMENT,
]

/**
 * A dev override pins 100% of traffic to a single arm, so it may only ever apply while
 * developing locally — never on a deploy (every deploy runs with `NODE_ENV=production`),
 * never under vitest, and never under Playwright.
 */
export const ALLOW_EXPERIMENT_OVERRIDES = IS_DEVELOPMENT_ENV && !IS_TESTING_ENV && !IS_PLAYWRIGHT_RUN

type ExperimentOverrides = Record<string, string | undefined>

/**
 * Local overrides keyed by experiment key, for developing/QAing a variant before
 * (or without) Amplitude targeting. Anything not listed here — and everything, once
 * deployed — resolves through Amplitude to the experiment's `defaultVariant`.
 */
const DEV_OVERRIDES: ExperimentOverrides = { [TICKET_DESCRIPTION_TOOLTIP_EXPERIMENT.key]: TICKET_DESCRIPTION_TOOLTIP_VARIANTS.tooltip }

/**
 * Overrides for Playwright, pinning every experiment to its default variant so E2E
 * asserts the control behavior. Applies to local runs as well as CI — otherwise a
 * `DEV_OVERRIDES` entry would silently put local E2E in a different arm than CI.
 */
const PLAYWRIGHT_OVERRIDES: ExperimentOverrides = Object.fromEntries(
  EXPERIMENTS.map(({ key, defaultVariant }) => [key, defaultVariant]),
)

export const EXPERIMENT_LOCAL_OVERRIDES: ExperimentOverrides = IS_PLAYWRIGHT_RUN
  ? PLAYWRIGHT_OVERRIDES
  : ALLOW_EXPERIMENT_OVERRIDES
    ? DEV_OVERRIDES
    : {}
